<template>
  <div class="order-info">
    <CCard v-if="loading && !order"><CCardBody>
      <div class="sk sk-line w-50"></div><div class="sk sk-line"></div><div class="sk sk-line w-75"></div><div class="sk sk-block"></div>
    </CCardBody></CCard>
    <CAlert v-else-if="loadError" color="danger">{{ loadError }} <CButton size="sm" color="link" @click="load">تلاش دوباره</CButton></CAlert>
    <template v-if="order">
      <CRow>
        <CCol col="12" lg="8">
          <CCard>
            <CCardHeader class="d-flex justify-content-between align-items-center">
              <strong>سفارش {{ order.code }}</strong>
              <span :class="['badge', 'badge-pill', 'px-3', 'py-2', order.status === 'cancelled' ? 'badge-danger' : 'badge-info']">{{ order.status_title }}</span>
            </CCardHeader>
            <CCardBody>
              <CRow class="small mb-3">
                <CCol col="6" md="4"><span class="text-muted">تاریخ ثبت:</span> {{ get_date_time(order.created_at) }}</CCol>
                <CCol col="6" md="4"><span class="text-muted">پرداخت:</span> {{ order.payment_method === 'cod' ? 'در محل' : 'آنلاین' }} — {{ order.payment_status === 'paid' ? 'پرداخت شده' : 'پرداخت نشده' }}</CCol>
                <CCol col="6" md="4"><span class="text-muted">ارسال:</span> {{ order.shipping_title || '—' }}</CCol>
                <CCol col="6" md="4" v-if="order.delivery_date"><span class="text-muted">زمان تحویل:</span> {{ order.delivery_date }}</CCol>
                <CCol col="6" md="4" v-if="order.coupon_code"><span class="text-muted">کد تخفیف:</span> {{ order.coupon_code }}</CCol>
              </CRow>
              <table class="table table-sm table-hover">
                <thead><tr><th></th><th>کالا</th><th>تعداد</th><th>قیمت واحد</th><th>جمع</th></tr></thead>
                <tbody>
                <tr v-for="it in order.items" :key="it.id">
                  <td style="width:56px"><img v-if="it.image" :src="it.image" alt="" class="oi-thumb"></td>
                  <td><a :href="productUrl(it)" target="_blank" rel="noopener">{{ it.name }}</a>
                    <div v-for="(b, i) in it.bundle" :key="i" class="small text-muted">{{ b.title || b.name }}: {{ b.value || b.option || '' }}</div></td>
                  <td>{{ it.qty }}</td>
                  <td>{{ get_currency(it.unit_sale_price || it.unit_price) }}
                    <div v-if="it.unit_sale_price && it.unit_sale_price < it.unit_price" class="small text-muted"><s>{{ get_currency(it.unit_price) }}</s></div></td>
                  <td>{{ get_currency(it.line_total) }}</td>
                </tr>
                </tbody>
              </table>
              <div class="oi-totals">
                <div><span>جمع کالاها</span><b>{{ get_currency(order.items_price) }} تومان</b></div>
                <div v-if="order.discount"><span>تخفیف</span><b class="text-danger">{{ get_currency(order.discount) }} تومان</b></div>
                <div v-if="order.coupon_discount"><span>کد تخفیف</span><b class="text-danger">{{ get_currency(order.coupon_discount) }} تومان</b></div>
                <div><span>هزینه ارسال</span><b>{{ order.shipping_price ? get_currency(order.shipping_price) + ' تومان' : 'رایگان' }}</b></div>
                <div class="oi-pay"><span>مبلغ قابل پرداخت</span><b>{{ get_currency(order.payable) }} تومان</b></div>
              </div>
              <p v-if="order.description" class="mt-3 mb-0"><span class="text-muted">توضیحات مشتری:</span> {{ order.description }}</p>
            </CCardBody>
          </CCard>
          <CCard>
            <CCardHeader><strong>تاریخچه وضعیت</strong></CCardHeader>
            <CCardBody>
              <p v-if="!order.log.length" class="text-muted mb-0">هنوز تغییری ثبت نشده است.</p>
              <ul class="oi-log">
                <li v-for="(l, i) in order.log" :key="i"><b>{{ l.status_title }}</b>
                  <span class="text-muted small"> — {{ get_date_time(l.created_at) }} {{ l.admin_name ? '· ' + l.admin_name : '' }}</span>
                  <div v-if="l.note" class="small">{{ l.note }}</div></li>
              </ul>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol col="12" lg="4">
          <CCard>
            <CCardHeader><strong>تغییر وضعیت</strong></CCardHeader>
            <CCardBody>
              <CSelect :value.sync="newStatus" :options="statuses.map(s => ({ value: s.id, label: s.title }))" label="وضعیت"/>
              <CTextarea v-model="statusNote" label="یادداشت (اختیاری)" rows="2"/>
              <CButton color="primary" block :disabled="busy || newStatus === order.status && !statusNote" @click="changeStatus">
                <CSpinner v-if="busy === 'status'" size="sm"/> ثبت وضعیت</CButton>
              <small class="text-muted d-block mt-2">لغو یا مرجوعی، موجودی کالاها را برمی‌گرداند.</small>
            </CCardBody>
          </CCard>
          <CCard>
            <CCardHeader><strong>کد رهگیری مرسوله</strong></CCardHeader>
            <CCardBody>
              <CInput v-model="tracking" placeholder="کد رهگیری پست"/>
              <CButton color="primary" variant="outline" block :disabled="!!busy" @click="saveTracking"><CSpinner v-if="busy === 'track'" size="sm"/> ذخیره</CButton>
            </CCardBody>
          </CCard>
          <CCard>
            <CCardHeader><strong>مشتری و آدرس</strong></CCardHeader>
            <CCardBody class="small">
              <p class="mb-1"><b>{{ order.customer.name || '—' }}</b> · {{ order.customer.mobile }}</p>
              <CButton v-if="order.customer.id" size="sm" color="link" class="p-0 mb-2" @click="$router.push('/dashboard/orders/list/' + order.customer.id)">سفارش‌های این مشتری</CButton>
              <p class="mb-1">گیرنده: {{ order.address.receiver_name }} — {{ order.address.receiver_mobile }}</p>
              <p class="mb-1">{{ order.address.province }}، {{ order.address.city }}، {{ order.address.address }}
                <span v-if="order.address.plaque">پلاک {{ order.address.plaque }}</span> <span v-if="order.address.unit">واحد {{ order.address.unit }}</span></p>
              <p class="mb-0" v-if="order.address.post_code">کد پستی: {{ order.address.post_code }}</p>
            </CCardBody>
          </CCard>
          <CCard>
            <CCardHeader><strong>یادداشت داخلی</strong></CCardHeader>
            <CCardBody>
              <CTextarea v-model="adminNote" rows="3" placeholder="فقط برای مدیران قابل مشاهده است"/>
              <CButton color="secondary" block :disabled="!!busy" @click="saveNote"><CSpinner v-if="busy === 'note'" size="sm"/> ذخیره یادداشت</CButton>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </template>
  </div>
</template>

<script>
import axios from "@/api/http";
import { apiToast } from "@/utils/confirm";

export default {
  name: 'OrderInfo',
  data() {
    return { order: null, statuses: [], loading: false, loadError: '', busy: '', newStatus: '', statusNote: '', tracking: '', adminNote: '' }
  },
  mounted() { this.load() },
  watch: { '$route.params.order_id'() { this.order = null; this.load() } },
  methods: {
    productUrl(it) { return String(localStorage.getItem('web_address') || '').replace(/\/+$/, '') + '/product/' + (it.product_slug || it.product_id) },
    load() {
      this.loading = true
      this.loadError = ''
      axios.post('/api/admin/order/get_order', { order_id: this.$route.params.order_id }).then((res) => {
        const d = res.data || {}
        if (d.error) { this.loadError = d.msg || 'سفارش یافت نشد'; return }
        this.order = d.order
        this.statuses = d.statuses || []
        this.newStatus = d.order.status
        this.tracking = d.order.tracking_code
        this.adminNote = d.order.admin_note
      }).catch((e) => { this.loadError = (e.response && e.response.data && e.response.data.msg) || 'دریافت سفارش ممکن نشد' })
        .finally(() => { this.loading = false })
    },
    act(key, url, body, after) {
      if (this.busy) return
      this.busy = key
      axios.post(url, { order_id: this.order.id, ...body }).then((res) => {
        apiToast(this, res)
        if (res.data && !res.data.error && after) after(res.data)
      }).catch((e) => console.log(e)).finally(() => { this.busy = '' })
    },
    changeStatus() {
      this.act('status', '/api/admin/order/change_order_status', { status: this.newStatus, note: this.statusNote }, () => { this.statusNote = ''; this.load() })
    },
    saveTracking() { this.act('track', '/api/admin/order/add_order_tracking_post', { tracking_code: this.tracking }) },
    saveNote() { this.act('note', '/api/admin/order/update_order_note', { admin_note: this.adminNote }) },
  },
}
</script>

<style scoped>
.oi-thumb { width: 48px; height: 48px; object-fit: contain; border-radius: 8px; background: #f8fafc; }
.oi-totals { max-width: 360px; margin-inline-start: auto; }
.oi-totals > div { display: flex; justify-content: space-between; padding: 4px 0; font-size: 13px; }
.oi-pay { border-top: 1px solid #e5e7eb; margin-top: 6px; padding-top: 8px !important; font-size: 15px !important; }
.oi-log { list-style: none; padding: 0; margin: 0; }
.oi-log li { border-inline-start: 2px solid var(--accent, #2563eb); padding: 2px 12px 10px; }
.sk { background: linear-gradient(90deg, #eef0f3 25%, #f6f7f9 37%, #eef0f3 63%); background-size: 400% 100%; animation: sk 1.4s ease infinite; border-radius: 6px; }
.sk-line { height: 14px; margin-bottom: 12px; }
.sk-block { height: 160px; }
.w-50 { width: 50%; } .w-75 { width: 75%; }
@keyframes sk { 0% { background-position: 100% 50% } 100% { background-position: 0 50% } }
</style>
